'use strict';

import 'es6-promise-promise';
import {callbackOnElements, createElWithAttrs} from '@borngroup/born-utilities';
import modules from './modules';
import pages from './pages';
import * as progress from '../components/progress';
import {ristrictNumberOfLinesInGiftMessage} from '../helpers/giftMessageValidate';

let lastOverlayTrigger;

export const
    globalWrapper = document.querySelector('.page'),
    headerEl = document.querySelector('[data-header-component="header"]'),
    headerContainerEl = document.querySelector('[data-header-component="header-container"]'),
    //Change these if needed.
    //These should be always synced with your "_variables.scss" file.
    breakpoints = {
        unit: 1,
        xxsmall: 320,
        xsmall: 480,
        small: 767,
        medium: 1024,
        xmedium: 1240,
        large: 1440
    },
    breakpointRanges = {
        'small': {max: breakpoints.small},
        'small-up': {min: breakpoints.small + breakpoints.unit},
        'medium-up': {min: breakpoints.medium + breakpoints.unit}
    };

/**
 * Loops and initializes all methods from an object, by running their 'init' property.
 * @param  {[Object]} obj [object literal with the methods to run]
 */
export function initMethods(obj, targetContainer) {
    var giftMsgTextAreaElement = document.querySelector('.personalisation__card-textarea');
    if (giftMsgTextAreaElement && window.SFRA.SitePreferences.enableGiftMessageLinesRestriction) {
        ristrictNumberOfLinesInGiftMessage();
    }
    for ( let key in obj ) {
        let currentProperty = obj[key];

        //If property is a function, run it immediately.
        if (typeof currentProperty === 'function') {
            currentProperty();
        } else {
            let query = currentProperty.default || key;

            if ((targetContainer || document).querySelector(query)) {
                currentProperty.init( query );
            }
        }
    }
}

export function initModules(targetContainer) {
    initMethods(modules, targetContainer);
}

export function initPages(targetContainer) {
    initMethods(pages, targetContainer);
}

/**
 * Publish an event at the specific target element scope
 * for other modules to subscribe.
 * The subscribe method can be a standard
 * .addEventListener('moduleName:eventName') method
 *
 * @param {String} moduleName
 * @param {String} eventName
 * @param {HTMLElement} target
 * @param {Object} detail object of arbitrary structure to pass to the event.
 */
export function publishEvent(moduleName, eventName, target, detail) {
    let event,
        params = { bubbles: true, cancelable: true, detail },
        eventString = moduleName && eventName ? `${moduleName}:${eventName}` : (moduleName || eventName);

    // IE >= 9, CustomEvent() constructor does not exist
    if (typeof window.CustomEvent !== 'function') {
        event = document.createEvent('CustomEvent');
        event.initCustomEvent(eventString, params.bubbles, params.cancelable, null);
    } else {
        event = new CustomEvent(eventString, params);
    }

    if (typeof target.dispatchEvent === 'function') {
        target.dispatchEvent(event);
    }
}

/**
 * Dispatches OOTB browser events.
 */
export function dispatchEvent(target, eventName) {
    let event;

    if (typeof window.Event === 'function') {
        event = new Event(eventName);
    } else {
        event = document.createEvent('Event');
        event.initEvent(eventName, true, true);
    }

    target.dispatchEvent(event);
}

function _getOverlayTarget(targetElement) {
    return targetElement && typeof targetElement === 'string' ? document.querySelector(targetElement) : (targetElement || globalWrapper);
}

/**
 * Helper function to set the 'set--overlay' class on a target element.
 * @param {[HTMLElement]} element
 */
export function setOverlay( options = {}) {
    let targetEl = _getOverlayTarget(options.targetElement);

    lastOverlayTrigger = options.trigger;

    targetEl.classList.add( 'set--overlay' );

    if (options.coverAll) {
        targetEl.classList.add( 'set--overlay-all' );
    }

    //Add an additional overlay in the main header element.
    //This is often desired in the case `options.coverAll` wouldn't work due to z-index constrains.
    //For example, `options.coverAll` sets a very high z-index, increasing the header element's z-index at this point wouldn't work, removing its z-index would work to "punch through", however if your header turns sticky while scrolling, it messes up the effect.
    if (options.includeHeader && options.targetElement !== headerContainerEl) {
        let headerOptions = objectAssign({}, options);

        headerOptions.targetElement = headerContainerEl;

        setOverlay(headerOptions);
    }
}

/**
 * Helper function to unset the 'set--overlay' class on a `targetElement`.
 * @param {[HTMLElement]} targetElement
 */
export function unsetOverlay( options = {} ) {
    let targetEl = _getOverlayTarget(options.targetElement);

    //Sometimes you want the overlay to stay on even when toggling between different functionalities (i.e. megamenu and search).
    //This conditional makes sure that if an `options.trigger` is available, it will only unset the overlay if
    //called with the same `options.trigger` value as previously set on `setOverlay()` on `lastOverlayTrigger`.
    if (!lastOverlayTrigger || !options.trigger || (lastOverlayTrigger === options.trigger)) {
        targetEl.classList.remove( 'set--overlay' );
        headerContainerEl.classList.remove( 'set--overlay' );
        targetEl.classList.remove( 'set--overlay-all' );
    }
}

/**
 * Attaches an event listener to a NodeList, list of elements, or if `elementList` is a query string generates a NodeList.
 * @param {[type]} elementList [description]
 * @param {[type]} eventName   [description]
 * @param {[type]} handler     [description]
 */
export function addEventListenerList(elements, eventName = 'click', handler) {
    callbackOnElements(elements, function(currentEl) {
        if (currentEl instanceof HTMLElement) {
            eventName.split(' ').forEach(currentEvent => currentEl.addEventListener(currentEvent, handler));
        }
    });
}

//Sorta polyfill for the Object.assign method, which is not supported in IE11.
//"inspired" by https://github.com/Raynos/xtend/blob/master/mutable.js
export function objectAssign(target) {
    for (let i = 1; i < arguments.length; i++) {
        let source = arguments[i];

        for (let key in source) {
            if (source.hasOwnProperty(key)) {
                target[key] = source[key];
            }
        }
    }

    return target;
}

/**
 * Checks if an element is currently consuming space in the DOM.
 * An element with, or a child of, display: none; will consume no space.
 * @param  {[type]} el [description]
 * @return {[type]}    [description]
 */
export function elConsumesSpace(el) {
    return el.offsetParent || (el.firstElementChild && el.firstElementChild.offsetHeight > 0);
}

/**
  * Create an alert to display the error message
  * @param {Object} message - Error message to display
  */
export function createErrorNotification(message, containerEl = '[data-checkout-component="error-container"]') {
    callbackOnElements(containerEl, function(currentEl) {
        currentEl.textContent = '';

        //if no message is provided, don't proceed and just clear the container.
        if (message) {
            createElWithAttrs(currentEl, {class: 'feedback-banner feedback-banner--error', role: 'alert'}, 'p', message);
        }
    });
}

export function handleError(err) {
    if (err.responseJSON.redirectUrl) {
        window.location.href = err.responseJSON.redirectUrl;
    } else {
        var errorMessage = (err && (err.hasOwnProperty('responseJSON') && err.responseJSON.hasOwnProperty('errorMessage'))) ? err.responseJSON.errorMessage : window.SFRA.Resources.genericErrorMessage;
        createErrorNotification(errorMessage);

        progress.hideLoader();
    }
}

/**
 * body-scroll-lock utility to allow scrolling within "locked" containers.
 */
export function allowScrollableAreas(el) {
    while (el && el !== document.body) {
        if (el.hasAttribute('data-scrollable')) {
            return true;
        }

        el = el.parentNode;
    }
}

/**
 * Toggles the `inert` attribute on the provided `elements`.
 */
export function toggleInert(elements, inert) {
    callbackOnElements(elements, function(currentEl) {
        inert ? currentEl.setAttribute('inert', '') : currentEl.removeAttribute('inert', '');
    });
}

/**
 * Export the current page's action and queryString data, since it might not always be available directly in the URL.
 */
export function getPageActionData() {
    let pageDataEl = document.querySelector('[data-querystring]');

    return pageDataEl ? {
        action: pageDataEl.getAttribute('data-action'),
        queryString: pageDataEl.getAttribute('data-querystring')
    } : null;
}

